import VideoPlaybackOptions from "@core/libs/video_playback_options";
import VoyoTimeline  from "../components/voyo_timeline/voyo_timeline";
import VoyoFF from "../components/voyo_seek/voyo_ff";
import VoyoRev from "../components/voyo_seek/voyo_rev";

export {};

type CatchUpBreak = {
    from: number;
    to: number;
    seen?: boolean;
    seenAt?: number;
};

type SeenBreaksByEpg = Record<string, Record<string, number>>;

type CatchUpNavigationItem = {
    sourceId: string;
    title: string;
    start: number;
    playbackOptions: VideoPlaybackOptions;
};

const epgsPlaceholder = document.getElementById('epgs_placeholder');
const epgPlaceholder = document.getElementById('epg_placeholder');

const AD_GRACE_MS = 60 * 1000;
const AD_DEBUG_GRACE_MS = 20 * 1000;
const AD_SEEN_EXPIRES_MS = 60 * 60 * 1000;
const AD_AUTO_SEEN_AFTER_MS = 72 * 60 * 60 * 1000;

// Pointer to fulslcreen epg splide. We need to resize it when we go into fullscreen.
let fullscreenEpgSplide: any = null;

// Which epg is currently playing. Needed to scroll to this epg when going or
// leaving fullscreen.
let currentEpgSourceId = 'live';

let adGraceUntil = 0;
let pendingSeekTo: number|null = null;
let activeBreak: CatchUpBreak|null = null;
let adDebugHTML = '';
let lastAdDebugLogAt = 0;
let seenBreaksByEpg: SeenBreaksByEpg = {};
let originalTitle = '';

function isCatchUpAdSkippingEnabled(): boolean {
    return config.country === 'si';
}

function isCatchUpAdDebugEnabled(): boolean {
    return isCatchUpAdSkippingEnabled() && config.devMode;
}

document.addEventListener('started', () => {
    if (app.playRestriction.restrictAppPlayback()) {
        return;
    }

    mountChannelsSplide();
    mountFullscreenSplides();
    initDayObserver();

    scrollToStartingEpg();
    updateCatchUpNavigation();

    if (isCatchUpAdSkippingEnabled()) {
        updateCurrentEpgSourceIdFromSelected();
        startAdGrace();
        setInterval(handleAdBreakPlayback, 500);
    }
});

document.addEventListener('started-user', () => {
    initCatchUpPlayer();
});

function initCatchUpPlayer(): void {
    if (!canPlayCatchUp()) {
        updateCatchUpVisibility();
        return;
    }

    const playbackOptions = JSON.parse(app.html.getData('video', 'playbackOptions') || '{}');
    bindCatchUpAutoPlayNext();
    loadPlayer({...playbackOptions, paused: true});
    setTimelineSeekHandler();
    setSeekButtonsSeekHandler();
}

document.addEventListener('fullscreen', () => {
    // Reset "show more" button when we go into fullscreen
    app.html.removeClass('.video__container', 'video__container--open');

    // Scroll to correct EPG when we go out of fullscreen
    scrollMainEpgsDiv(currentEpgSourceId);
});

function mountChannelsSplide(): void {
    if (typeof Splide === 'undefined') {
        return;
    }

    new Splide('.splide.channels').mount();
}

function mountFullscreenSplides(): void {
    if (typeof Splide === 'undefined') {
        return;
    }

    new Splide('.splide.channels-fullscreen').mount();

    fullscreenEpgSplide = new Splide('.splide.epgs_fullscreen').mount();

    // This marks correct day in fullscreen view.
    fullscreenEpgSplide.on('active', (s: any) => {
        if (!s.index) {
            return;
        }
        const tvDay = app.html.getData(s.slide, 'tvDay');
        updateSelectedDay(tvDay);
    });
}

// Scrolls epg divs to the show user wants to see (for example, comming from search page)
function scrollToStartingEpg(): void {
    const selectedEpg = app.html.q('.epg-item.selected:not(:first-child)');
    if (!selectedEpg) {
        return;
    }

    const tvDay = app.html.getData(selectedEpg, 'tvDay');
    updateSelectedDay(tvDay);

    currentEpgSourceId = app.html.getData(selectedEpg, 'sourceId');
    scrollMainEpgsDiv(currentEpgSourceId, 'instant');
    scrollFullscreenEpgsDiv(currentEpgSourceId);
}

function updateCurrentEpgSourceIdFromSelected(): void {
    const selectedEpg = app.html.q('.epg-item.selected') as HTMLElement | null;
    if (!selectedEpg) {
        return;
    }

    currentEpgSourceId = app.html.getData(selectedEpg, 'sourceId');
}

// "Show more" — in fullscreen mode, show or hide EPG list
function toggleEpgNav(): void {
    if (!window.app) {
        return;
    }

    app.html.toggleClass('.video__container', 'video__container--open');
    
    const isOpening = app.html.hasClass('.video__container', 'video__container--open');
    if (isOpening) {
        scrollFullscreenEpgsDiv(currentEpgSourceId);
    }
}

// Creates new observer that will track which day is in user's view
function initDayObserver(): void {
    app.observer.init({root: epgsPlaceholder, rootMargin: '0px 0px -75% 0px', threshold: 0});
}

// Mark the day that is currently visible in right scrollbar
function markVisibleDay(task: any): void {
    if (!task.isVisible) {
        return;
    }

    const day = app.html.getData(task.element as HTMLElement, 'epgDay');
    updateSelectedDay(day);
}

// Scrolls the EPGs list to a selected catch-up day and marks this
// given day as selected
function scrollToCatchUpDay(day: string): void {
    if (!window.app) {
        return;
    }

    updateSelectedDay(day);

    scrollMainEpgsDiv(day);
    scrollFullscreenEpgsDiv(day);
}

// Scrolls epgs div that is shown in fullscreen to the element with given tvDay or sourceId.
// It's a splide carousel so we move by invoking it's go() api
function scrollFullscreenEpgsDiv(scrollTo: string): void {
    if (!fullscreenEpgSplide) {
        return
    }

    const index = app.html.qAll('.epgs_fullscreen [data-tv-day]')
        .findIndex(el => {
            return app.html.getData(el, 'tvDay') === scrollTo ||
                app.html.getData(el, 'sourceId') === scrollTo;
        });

    fullscreenEpgSplide.go(index);
}

// Scrolls main epgs div to the specified tvDay or to the specified sourceId.
// This is ordinary div and we scroll it up or down.
function scrollMainEpgsDiv(scrollTo: string, behavior = 'smooth'): void {
    const scrollEl = app.html
        .qAll('.epg-item')
        .find(el => {
            return app.html.getData(el, 'tvDay') === scrollTo ||
                app.html.getData(el, 'sourceId') === scrollTo;
        });

    if (!scrollEl || !epgsPlaceholder) {
        return;
    }

    const placeholderRect = epgsPlaceholder.getBoundingClientRect();
    const toRect = scrollEl.getBoundingClientRect();

    epgsPlaceholder.scrollTo({
        top: epgsPlaceholder.scrollTop + toRect.top - placeholderRect.top - 40,
        behavior: behavior as ScrollBehavior,
    });
}

// Selects an EPG row, updates the page state, and loads its player partial.
function playCatchUpEpg(sourceId: string, playbackOptions: VideoPlaybackOptions = {}): void {
    if (!window.app) {
        return;
    }

    currentEpgSourceId = sourceId;

    selectEpg(sourceId);
    updateEpgInfo(sourceId);
    updateCatchUpNavigation();

    loadPlayer(playbackOptions);
}

function playPrevNextCatchUp(direction: number): void {
    if (!window.app) {
        return;
    }

    const prevNext = getPrevNextCatchUp(direction);
    if (!prevNext) {
        return;
    }

    playCatchUpEpg(prevNext.sourceId, prevNext.playbackOptions);
    scrollMainEpgsDiv(prevNext.sourceId);
    scrollFullscreenEpgsDiv(prevNext.sourceId);
}

function getPrevNextCatchUp(direction: number): CatchUpNavigationItem|null {
    const items = getCatchUpNavigationItems();
    const currentIndex = items.findIndex(item => item.sourceId === currentEpgSourceId);
    if (currentIndex < 0) {
        return null;
    }

    return items[currentIndex + direction] || null;
}

function getCatchUpNavigationItems(): Array<CatchUpNavigationItem> {
    return app.html.qAll('.epg-item[data-source-id]')
        .map(element => {
            const sourceId = app.html.getData(element, 'sourceId');
            const isLive = app.html.getData(element, 'isLive') === 'true' || sourceId === 'live';
            if (!sourceId || isLive) {
                return null;
            }

            return {
                sourceId,
                title: app.html.getData(element, 'title'),
                start: +(app.html.getData(element, 'start') || 0),
                playbackOptions: app.html.safeJsonParse(
                    app.html.getData(element, 'playbackOptions') || '{}'
                ) as VideoPlaybackOptions,
            };
        })
        .filter((item): item is CatchUpNavigationItem => !!item)
        .sort((a, b) => a.start - b.start);
}

function updateCatchUpNavigation(): void {
    updateCatchUpNavigationButton('#catchup_previous', getPrevNextCatchUp(-1));
    updateCatchUpNavigationButton('#catchup_next', getPrevNextCatchUp(1));
}

function updateCatchUpNavigationButton(selector: string, item: CatchUpNavigationItem|null): void {
    const button = app.html.q(selector) as HTMLButtonElement | null;
    if (!button) {
        return;
    }

    if (!item) {
        app.html.addClass(button, 'hidden');
        button.disabled = true;
        return;
    }

    const label = app.html.getData(button, 'label');
    const title = app.html.q('.catchup-prev-next-title', button);
    if (title) {
        title.textContent = item.title.length > 30 ? item.title.slice(0, 29) + '…' : item.title;
    }

    button.title = label ? `${label}: ${item.title}` : item.title;
    button.setAttribute('aria-label', button.title);
    button.disabled = false;
    app.html.removeClass(button, 'hidden');
}

// Marks one EPG row as selected.
function selectEpg(sourceId: string): void {
    sourceId === 'live' ? app.html.addClass('.player', 'live') : app.html.removeClass('.player', 'live');

    app.html.removeClass('.epg-item.selected', 'selected');
    app.html.addClass('.epg-item[data-source-id="' + sourceId + '"]', 'selected');

    app.html.removeClass('.epgs_fullscreen .selected', 'selected');
    app.html.addClass('.epgs_fullscreen .card[data-source-id="' + sourceId + '"]', 'selected');
}

// Copies the prerendered EPG info template into the visible info panel.
function updateEpgInfo(sourceId: string): void {
    const epgEl = app.html.q('.epg-item[data-source-id="' + sourceId + '"]');
    const infoTemplate = app.html.q('template[data-epg-info]', epgEl) as HTMLTemplateElement | null;

    if (infoTemplate && epgPlaceholder) {
        app.html.writeHTML(epgPlaceholder, infoTemplate.innerHTML);
    }
}

// Marks the active day tab.
function updateSelectedDay(day: string): void {
    app.html.removeClass('.epg__day.selected', 'selected');
    app.html.addClass(`.epg__day[data-uniq="${day}"]`, 'selected');
}

// Fetches and initializes the selected EPG player partial.
function loadPlayer(playbackOptions: VideoPlaybackOptions = {}): void {
    if (!canPlayCatchUp()) {
        updateCatchUpVisibility();
        return;
    }

    updateCatchUpVisibility();
    app.html.writeHTML('.title_text', playbackOptions.title || '');
    originalTitle = playbackOptions.originalTitle || '';
    updateOriginalTitleText(originalTitle);

    pendingSeekTo = null;
    activeBreak = null;
    clearAdDebugBreaks();
    app.voyoVideo.setControlsFrozen(false);

    app.voyoVideo.stopVideoPlayers();
    app.voyoVideo.initForCatchUp('.player_box video', playbackOptions)
        .then(() => {
            app.html.q('.player')?.focus({preventScroll: true});
        });
}

function bindCatchUpAutoPlayNext(): void {
    const video = app.html.q('.player_box video') as HTMLMediaElement | null;
    if (!video) {
        return;
    }

    video.addEventListener('ended', playNextCatchUp);
}

function playNextCatchUp(): void {
    if (getPrevNextCatchUp(1)) {
        playPrevNextCatchUp(1);
    }
}

function updateCatchUpVisibility(): void {
    const playerEl = app.html.q('#epg_player_placeholder') as HTMLElement | null;
    const noLoginEl = app.html.q('#epg_no_login_placeholder') as HTMLElement | null;
    const noSubscriptionEl = app.html.q('#epg_no_subscription_placeholder') as HTMLElement | null;
    const user = app.user.user;

    if (!playerEl || !noLoginEl || !noSubscriptionEl) {
        return;
    }

    if (canPlayCatchUp()) {
        showCatchUpPlaceholder(playerEl);
        hideCatchUpPlaceholder(noLoginEl);
        hideCatchUpPlaceholder(noSubscriptionEl);
        return;
    }

    app.voyoVideo.stopVideoPlayers();
    hideCatchUpPlaceholder(playerEl);

    if (!user) {
        showCatchUpPlaceholder(noLoginEl);
        hideCatchUpPlaceholder(noSubscriptionEl);
        return;
    }

    hideCatchUpPlaceholder(noLoginEl);
    showCatchUpPlaceholder(noSubscriptionEl);
}

function showCatchUpPlaceholder(element: HTMLElement): void {
    element.style.display = 'block';
}

function hideCatchUpPlaceholder(element: HTMLElement): void {
    element.style.display = 'none';
}

function canPlayCatchUp(): boolean {
    return !!app.user.user?.isSubscribed;
}

function setTimelineSeekHandler(): void {
    const timeline = app.html.q('voyo-timeline') as VoyoTimeline | null;
    if (!timeline) {
        return;
    }

    timeline.registerSeekHandler((time: number) => {
        seekWithAdBreakRules(time);
    });
}

function setSeekButtonsSeekHandler(): void {
    const rev = app.html.q('voyo-rev') as VoyoRev | null;
    const ff = app.html.q('voyo-ff') as VoyoFF | null;
    if (rev) {
        rev.registerSeekHandler((seconds: number) => {
            seekRelativeWithAdBreakRules(-seconds);
        });
    }

    if (ff) {
        ff.registerSeekHandler((seconds: number) => {
            seekRelativeWithAdBreakRules(seconds);
        });
    }
}

function seekRelativeWithAdBreakRules(seconds: number): void {
    const currentTime = app.voyoVideo.getCurrentTime();
    const target = Math.round(currentTime + seconds);

    seekWithAdBreakRules(target);
    app.voyoVideo.throttledShowVideoControls();
}

function seekWithAdBreakRules(targetTime: number): void {
    if (!isCatchUpAdSkippingEnabled()) {
        app.voyoVideo.setVideoPlayback(targetTime);
        return;
    }

    const currentTime = app.voyoVideo.getCurrentTime();
    const currentBreak = getBreakAt(currentTime);

    if (!isAdGraceActive() && currentBreak && !isBreakSeen(currentBreak)) {
        return;
    }

    const skippedBreak = getSkippedBreak(currentTime, targetTime);
    if (!isAdGraceActive() && skippedBreak) {
        activeBreak = skippedBreak;
        pendingSeekTo = targetTime;
        app.voyoVideo.setVideoPlayback(skippedBreak.from);
        app.voyoVideo.setControlsFrozen(true);
        updateOriginalTitleText(getAdTitle());
        drawAdDebugBreaks();
        return;
    }

    app.voyoVideo.setVideoPlayback(targetTime);
}

function handleAdBreakPlayback(): void {
    const currentTime = app.voyoVideo.getCurrentTime();
    if (currentTime < 0) {
        return;
    }

    applySeenBreakMemory();
    expireOldBreaks();
    drawAdDebugBreaks();
    updateAdDebugInfo();
    logAdDebugState(currentTime);

    if (isAdGraceActive()) {
        app.voyoVideo.setControlsFrozen(false);
        updateOriginalTitleText(originalTitle);
        return;
    }

    const currentBreak = activeBreak || getBreakAt(currentTime);
    if (!currentBreak || isBreakSeen(currentBreak)) {
        activeBreak = null;
        app.voyoVideo.setControlsFrozen(false);
        updateOriginalTitleText(originalTitle);
        return;
    }

    activeBreak = currentBreak;
    app.voyoVideo.setControlsFrozen(true);
    updateOriginalTitleText(getAdTitle());

    if (currentTime < currentBreak.to - 1) {
        return;
    }

    markBreakSeen(currentBreak);
    app.voyoVideo.setControlsFrozen(false);
    updateOriginalTitleText(originalTitle);
    startAdGrace();

    if (pendingSeekTo !== null) {
        const seekTo = pendingSeekTo;
        pendingSeekTo = null;
        app.voyoVideo.setVideoPlayback(seekTo);
    }

    activeBreak = null;
    drawAdDebugBreaks();
}

function startAdGrace(): void {
    adGraceUntil = Date.now() + getAdGraceMs();
}

function isAdGraceActive(): boolean {
    return Date.now() < adGraceUntil;
}

function getAdGraceMs(): number {
    return isCatchUpAdDebugEnabled() ? AD_DEBUG_GRACE_MS : AD_GRACE_MS;
}

function updateOriginalTitleText(title: string): void {
    const titleEl = app.html.q('.originaltitle_text') as HTMLElement | null;
    if (!titleEl) {
        return;
    }

    titleEl.textContent = title;
}

function getAdTitle(): string {
    return '{{ T "catch_up.ad_resume" }} ' + originalTitle;
}

function getBreaks(): Array<CatchUpBreak> {
    return (app.voyoVideo.getPlaybackOptions().breaks || []) as Array<CatchUpBreak>;
}

function getBreakAt(time: number): CatchUpBreak|null {
    return getBreaks().find(b => b.from <= time && b.to > time) || null;
}

function getSkippedBreak(from: number, to: number): CatchUpBreak|null {
    if (to <= from) {
        return null;
    }

    return getBreaks().find(b => {
        return !isBreakSeen(b) && b.from > from && b.to <= to;
    }) || null;
}

function isBreakSeen(breakItem: CatchUpBreak): boolean {
    if (isBreakOldEnoughToSkip(breakItem)) {
        return true;
    }

    return !!breakItem.seen && !!breakItem.seenAt && Date.now() - breakItem.seenAt < AD_SEEN_EXPIRES_MS;
}

function markBreakSeen(breakItem: CatchUpBreak): void {
    breakItem.seen = true;
    breakItem.seenAt = Date.now();

    if (!seenBreaksByEpg[currentEpgSourceId]) {
        seenBreaksByEpg[currentEpgSourceId] = {};
    }

    seenBreaksByEpg[currentEpgSourceId][breakKey(breakItem)] = breakItem.seenAt;
}

function expireOldBreaks(): void {
    Object.keys(seenBreaksByEpg).forEach(sourceId => {
        Object.keys(seenBreaksByEpg[sourceId]).forEach(key => {
            if (Date.now() - seenBreaksByEpg[sourceId][key] >= AD_SEEN_EXPIRES_MS) {
                delete seenBreaksByEpg[sourceId][key];
            }
        });
    });

    getBreaks().forEach(b => {
        if (b.seenAt && Date.now() - b.seenAt >= AD_SEEN_EXPIRES_MS) {
            b.seen = false;
            b.seenAt = 0;
        }
    });
}

function applySeenBreakMemory(): void {
    const seenBreaks = seenBreaksByEpg[currentEpgSourceId] || {};

    getBreaks().forEach(b => {
        const seenAt = seenBreaks[breakKey(b)];
        if (!seenAt) {
            return;
        }

        b.seen = true;
        b.seenAt = seenAt;
    });
}

function isBreakOldEnoughToSkip(breakItem: CatchUpBreak): boolean {
    const chunkStart = app.voyoVideo.getPlaybackOptions().chunkStart;
    if (!chunkStart) {
        return false;
    }

    const breakEndedAtMs = (chunkStart + breakItem.to) * 1000;
    return Date.now() - breakEndedAtMs >= AD_AUTO_SEEN_AFTER_MS;
}

function drawAdDebugBreaks(): void {
    const debugEl = app.html.q('.catchup-ad-debug') as HTMLElement | null;
    const options = app.voyoVideo.getPlaybackOptions();
    const length = options.length || 0;

    const breaks = getBreaks();

    if (!debugEl) {
        return;
    }

    if (!isCatchUpAdDebugEnabled() || !length || !breaks.length) {
        clearAdDebugBreaks(debugEl);
        return;
    }

    app.html.removeClass(debugEl, 'hidden');

    const html = breaks.map(b => {
        const left = Math.max(0, Math.floor(100 * b.from / length));
        const width = Math.max(1, Math.ceil(100 * (b.to - b.from) / length));
        const className = isBreakSeen(b) ? 'seen' : (b === activeBreak ? 'active' : 'unseen');

        return `<span class="${className}" style="left:${left}%;width:${width}%"></span>`;
    }).join('');

    if (html !== adDebugHTML) {
        adDebugHTML = html;
        app.html.writeHTML(debugEl, html);
    }
}

function clearAdDebugBreaks(debugEl: HTMLElement | null = null): void {
    adDebugHTML = '';

    debugEl = debugEl || app.html.q('.catchup-ad-debug') as HTMLElement | null;
    if (!debugEl) {
        return;
    }

    app.html.addClass(debugEl, 'hidden');
    app.html.writeHTML(debugEl, '');
}

function updateAdDebugInfo(): void {
    const debugEl = app.html.q('.catchup-ad-debug-info') as HTMLElement | null;
    if (!debugEl) {
        return;
    }

    if (!isCatchUpAdDebugEnabled()) {
        app.html.addClass(debugEl, 'hidden');
        return;
    }

    app.html.removeClass(debugEl, 'hidden');
    app.html.writeHTML(debugEl, `grace: ${secondsLeft(adGraceUntil)}s`);
}

function logAdDebugState(currentTime: number): void {
    if (!isCatchUpAdDebugEnabled()) {
        return;
    }

    const nowSec = Math.floor(Date.now() / 1000);
    if (lastAdDebugLogAt === nowSec) {
        return;
    }

    lastAdDebugLogAt = nowSec;

    console.groupCollapsed('[catchup ads]', {
        time: Math.round(currentTime),
        graceLeft: secondsLeft(adGraceUntil),
        activeBreak: activeBreak ? breakLabel(activeBreak) : '-',
        pendingSeekTo: pendingSeekTo ?? '-',
        sourceId: currentEpgSourceId,
    });

    console.table(getBreaks().map((b, index) => {
        return {
            index,
            from: b.from,
            to: b.to,
            status: isBreakOldEnoughToSkip(b) ? 'seen_72h' : (isBreakSeen(b) ? 'seen' : 'unseen'),
            active: b === activeBreak,
            seenExpiresIn: b.seenAt ? secondsLeft(b.seenAt + AD_SEEN_EXPIRES_MS) : '-',
        };
    }));

    console.groupEnd();
}

function secondsLeft(until: number): number {
    return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

function breakLabel(breakItem: CatchUpBreak): string {
    return breakItem.from + '-' + breakItem.to;
}

function breakKey(breakItem: CatchUpBreak): string {
    return breakItem.from + '-' + breakItem.to;
}

window.scrollToCatchUpDay = scrollToCatchUpDay;
window.playCatchUpEpg = playCatchUpEpg;
window.playPrevNextCatchUp = playPrevNextCatchUp;
window.toggleEpgNav = toggleEpgNav;
window.epgMarkVisibleDay = markVisibleDay;
